import { useEffect, useState } from 'react';

import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { CondicaoDeCobranca, Orcamento, StatusDoOrcamento } from '@/data/tipos';
import { atual } from '@/session/sessao';
import { AprovarOrcamentoModal } from './AprovarOrcamentoModal';
import { DetalheDoOrcamentoModal } from './DetalheDoOrcamentoModal';
import { NovoOrcamentoModal } from './NovoOrcamentoModal';
import { imprimirOrcamento } from './imprimirOrcamento';

export interface OrcamentoAbertoModalProps {
    orcamentoId: string;
    onFechar: () => void;
    /** Algo mudou (situação, edição, cópia): quem abriu recarrega a lista. */
    onMudou: () => void;
}

/**
 * O detalhe do orçamento aberto fora de Vendas (a aba Orçamentos do paciente),
 * com as mesmas ações: editar, duplicar, PDF e mudar a situação.
 *
 * A linha da lista traz só o resumo; o detalhe lê o orçamento com os itens.
 */
export function OrcamentoAbertoModal({ orcamentoId, onFechar, onMudou }: OrcamentoAbertoModalProps) {
    const aviso = useAviso();
    const [orcamento, setOrcamento] = useState<Orcamento | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [editando, setEditando] = useState<Orcamento | null>(null);
    const [duplicando, setDuplicando] = useState<Orcamento | null>(null);
    const [aprovando, setAprovando] = useState<Orcamento | null>(null);
    const [versao, setVersao] = useState(0);

    useEffect(() => {
        let vivo = true;
        dados().orcamento(orcamentoId)
            .then((o) => { if (vivo) { setOrcamento(o); setErro(null); } })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Orçamento não encontrado.'); });
        return () => { vivo = false; };
    }, [orcamentoId, versao]);

    const recarregar = () => {
        setVersao((v) => v + 1);
        onMudou();
    };

    const decidir = async (o: Orcamento, status: StatusDoOrcamento, cobranca?: CondicaoDeCobranca): Promise<boolean> => {
        if (status === 'aprovado' && !cobranca) {
            setAprovando(o);
            return false;
        }
        try {
            await dados().decidirOrcamento(o.id, status, cobranca);
            aviso({
                title: status === 'aprovado' ? 'Orçamento aprovado' : status === 'enviado' ? 'Orçamento enviado' : 'Orçamento recusado',
                description: `${o.numero} · ${o.paciente}`,
                tone: status === 'recusado' ? 'warning' : 'success',
            });
            recarregar();
            return true;
        } catch (e) {
            aviso({ title: 'Não foi possível mudar a situação', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
            return false;
        }
    };

    const gerarPdf = (o: Orcamento) => {
        if (!imprimirOrcamento(o, atual()?.clinica ?? 'Clínica')) {
            aviso({ title: 'Permita pop-ups para gerar o PDF', description: 'O navegador bloqueou a janela de impressão.', tone: 'warning' });
        }
    };

    if (aprovando) {
        return (
            <AprovarOrcamentoModal
                key={aprovando.id} orcamento={aprovando}
                onFechar={() => setAprovando(null)}
                onAprovar={(cobranca) => decidir(aprovando, 'aprovado', cobranca)}
            />
        );
    }
    if (editando) {
        return (
            <NovoOrcamentoModal
                key={`editar-${editando.id}`} open orcamento={editando}
                onClose={() => setEditando(null)} onCriado={recarregar}
            />
        );
    }
    if (duplicando) {
        return (
            <NovoOrcamentoModal
                key={`duplicar-${duplicando.id}`} open base={duplicando}
                onClose={() => setDuplicando(null)}
                onCriado={() => { onMudou(); onFechar(); }}
            />
        );
    }
    /* Enquanto lê, nada aparece: o modal abre já com o orçamento, ou com o erro. */
    if (!orcamento && !erro) return null;
    return (
        <DetalheDoOrcamentoModal
            orcamento={orcamento} erro={erro}
            onFechar={onFechar}
            onEditar={setEditando}
            onDuplicar={setDuplicando}
            onGerarPdf={gerarPdf}
            onDecidir={(o, status) => { void decidir(o, status); }}
        />
    );
}
